import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import FetchErrorState from "./components/FetchErrorState";
import WorkspaceHeader from "./components/WorkspaceHeader";
import { WorkspaceCard } from "./components/CurrentWorkspaceCard";
import { Dialog } from "components/dialog";
import { useAuthContext } from "auth/AuthProvider";
import { setAccessToken } from "auth/tokenStorage";
import { WorkspaceDialogContent } from "./components/WorkspaceDialogContent";
import { MyWorkspacesResponse, Workspace } from "types";
import Loader from "components/Loader";
import api from "api/axios";
import toast from "react-hot-toast";

const WorkspacesView = () => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSwitching, setIsSwitching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(
    null
  );
  const { user, fetchUser } = useAuthContext();
  const navigate = useNavigate();
  const [isLoadingCreateOrEdit, setIsLoadingCreateOrEdit] =
    useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const fetchWorkspaces = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<MyWorkspacesResponse>(
        "/workspaces/my-workspaces"
      );
      const mappedWorkspaces = response.data.workspaces.map((ws) => ({
        id: ws.workspace_id,
        name: ws.workspace_name,
        organization_name: ws.organization_name,
        is_current: ws.is_current,
      }));
      setWorkspaces(mappedWorkspaces);
    } catch (error) {
      setError("Failed to load workspaces. Please try again.");
      setWorkspaces([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  const handleSwitch = async (workspaceId: string) => {
    setIsSwitching(true);
    try {
      const response = await api.post(`/workspaces/switch/${workspaceId}`);
      const { access_token, expires_in } = response.data;
      setAccessToken(access_token, expires_in);
      await fetchUser();
      navigate("/dashboard/agents");
    } catch (error) {
      setError("Failed to switch workspace. Please try again later.");
    } finally {
      setIsSwitching(false);
    }
  };

  const handleOpenCreate = () => {
    setSelectedWorkspace(null);
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (workspace: Workspace) => {
    setSelectedWorkspace(workspace);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedWorkspace(null);
  };

  const handleCreateWorkspace = async (data: { name: string }) => {
    setIsLoadingCreateOrEdit(true);
    setSubmitError(null);
    try {
      await api.post("/workspaces", { name: data.name });
      setIsDialogOpen(false);
      await fetchWorkspaces();
    } catch (error: any) {
      setSubmitError(
        error.response?.data?.message ||
          "Failed to create workspace. Please try again later."
      );
    } finally {
      setIsLoadingCreateOrEdit(false);
    }
  };

  // Mock API call for updating a workspace
  const handleUpdateWorkspace = async (data: { name: string }) => {
    if (!selectedWorkspace) return;
    setIsLoadingCreateOrEdit(true);
    try {
      await api.put(`/workspaces/${selectedWorkspace.id}`, {
        name: data.name,
      });
      toast.success("Workspace updated successfully");
      await fetchWorkspaces();
    } catch (error) {
      toast.error("Workspace updated fail");

      console.error("Failed to update workspace:", error);
    } finally {
      setIsLoadingCreateOrEdit(false);
      handleCloseDialog();
    }
  };

  const isEditMode = selectedWorkspace !== null;

  return (
    <div className="relative mt-12 min-h-screen p-6 md:mt-0">
      <WorkspaceHeader handleCreateNew={handleOpenCreate} />
      {loading && (
        <Loader
          txt={isSwitching ? "Switching workspace..." : "Loading workspaces..."}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        />
      )}
      {error && !loading && (
        <FetchErrorState error={error} onRetry={fetchWorkspaces} />
      )}
      {/* Workspace Cards */}
      {!loading && !error && (
        <div className="flex flex-wrap gap-3">
          {[...workspaces].map((ws) => (
            <WorkspaceCard
              key={ws.id}
              name={ws.name}
              isCurrent={ws.is_current}
              onSwitch={ws.is_current ? undefined : () => handleSwitch(ws.id)}
              onEdit={() => handleOpenEdit(ws)}
            />
          ))}
        </div>
      )}

      <Dialog
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
        backdropHandlerDisabled={true}
        children={
          <WorkspaceDialogContent
            loading={isLoadingCreateOrEdit}
            onCancel={handleCloseDialog}
            onSubmit={
              isEditMode ? handleUpdateWorkspace : handleCreateWorkspace
            }
            isEditMode={isEditMode}
            initialName={selectedWorkspace?.name}
            error={submitError}
          />
        }
      />
    </div>
  );
};

export default WorkspacesView;
